import { useState, useEffect, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  fetchPollByAny,
  respondToPollAsync,
  selectCurrentPoll,
  selectPollsLoading,
} from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import { selectUserInfo } from "@/store/redux/slices/auth/auth_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Button from "@/components/common/Button";
import Success from "@/components/common/alerts/Success";
import Navbar from "@/pages/admin/polls/Navbar";
import DynamicPollsLoginModal from "@/pages/admin/polls/Dynamic_Polls_Login_Modal";
import Avatar from "@/components/common/Avatar";
import DefaultHeroImg from "@/assets/images/presidential_candidates.png";

// Shown in the background while the user is not yet authenticated / poll not loaded
const PLACEHOLDER_POLL = {
  name: "Survey",
  description: "Log into your account to view and respond to this survey.",
  media: DefaultHeroImg,
  options: [
    { _id: "ph1", name: "Option A" },
    { _id: "ph2", name: "Option B" },
    { _id: "ph3", name: "Option C" },
  ],
};

const PollOptionRow = ({ option, selected, onSelect }) => (
  <label className="flex items-center justify-between p-4 mb-5 border rounded-lg cursor-pointer hover:bg-gray-50 last:mb-0">
    <p className="font-medium text-darkGrey">{option.name}</p>
    <input
      type="radio"
      name="poll-option"
      className="w-5 h-5 accent-[#0F75BC]"
      checked={selected}
      value={option._id || option.name}
      onChange={() => onSelect(option)}
    />
  </label>
);

PollOptionRow.propTypes = {
  option: PropTypes.shape({
    name: PropTypes.string,
    _id: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const Polls = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const currentPoll = useSelector(selectCurrentPoll);
  const isLoading = useSelector(selectPollsLoading);
  const userInfo = useSelector(selectUserInfo);
  const { showLoader, hideLoader } = useLoader();

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loginModalDismissed, setLoginModalDismissed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const canRespond = isAuthenticated || isAnonymous;
  const showLoginModal = !canRespond && !loginModalDismissed;

  useEffect(() => {
    if (!id || !canRespond) return;

    const fetchPoll = async () => {
      showLoader();
      try {
        await dispatch(fetchPollByAny({ key: "_id", value: id })).unwrap();
      } catch (error) {
        console.error("Error fetching poll:", error);
      } finally {
        hideLoader();
      }
    };

    fetchPoll();
  }, [dispatch, hideLoader, id, canRespond, showLoader]);

  const pollOptions = useMemo(() => {
    if (!currentPoll?.options) return [];
    const opts = currentPoll.options;
    return Array.isArray(opts) ? opts : opts?.name ? [opts] : [];
  }, [currentPoll]);

  const respondentDisplay = useMemo(() => {
    if (isAnonymous) {
      return { name: "Anonymous", avatar: null, role: "Respondent" };
    }
    if (isAuthenticated && userInfo?.fullname) {
      return {
        name: userInfo.fullname,
        avatar: userInfo.profileImage || null,
        role: "Respondent",
      };
    }
    return { name: "Anonymous", avatar: null, role: "Respondent" };
  }, [isAnonymous, isAuthenticated, userInfo]);

  const handleDismissLoginModal = () => {
    setLoginModalDismissed(true);
    setIsAnonymous(false);
  };

  const handleLoginFailed = () => {
    setLoginModalDismissed(false);
  };

  const handleProceedAnonymously = () => {
    setIsAnonymous(true);
    setLoginModalDismissed(true);
  };

  const handleSubmit = async () => {
    if (!selectedOption || !canRespond) return;

    const body = {
      surveyPollsID: currentPoll?._id || id,
      optionID: selectedOption._id,
      userID: userInfo?.userID || "",
      fullname: isAnonymous ? "Anonymous" : (userInfo?.fullname || ""),
      email: isAnonymous ? "" : (userInfo?.email || ""),
      profileImage: isAnonymous ? "" : (userInfo?.profileImage || ""),
      state: userInfo?.state || "",
      localGovernmentArea: userInfo?.localGovernmentArea || "",
      ward: userInfo?.ward || "",
      pollingUnit: userInfo?.pollingUnit || "",
    };

    showLoader();
    try {
      await dispatch(respondToPollAsync({ pollId: id, body })).unwrap();
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to submit poll response:", error);
    } finally {
      hideLoader();
    }
  };

  if (!id) {
    return <Navigate to="/signin" replace />;
  }

  // Use real poll data once loaded, otherwise show the placeholder so the
  // background is always visible while the login modal is on screen.
  const displayPoll = currentPoll || PLACEHOLDER_POLL;
  const pollNotFound = isAuthenticated && !isLoading && !currentPoll;

  const breadcrumbItems = [
    { link: "", title: "Survey" },
    { link: "", title: displayPoll.name },
  ];

  const heroImage = displayPoll.media || DefaultHeroImg;

  return (
    <div>
      <Navbar respondentDisplay={respondentDisplay} />
      <div className="bg-primary min-h-[300px] text-left pb-16">
        <div className="max-w-[1200px] mx-auto px-4 relative top-20">
          <Breadcrumb items={breadcrumbItems} textColor="white" />

          {pollNotFound ? (
            <div className="min-h-[50vh] flex items-center justify-center text-white text-lg font-medium">
              Poll not found
            </div>
          ) : (
            <div className="grid my-5 md:gap-10 md:grid-cols-3 items-start">
              <div className="md:col-span-2">
                <img
                  src={heroImage}
                  alt={displayPoll.name}
                  className="w-full rounded-lg object-cover max-h-[320px]"
                />
                <div className="p-5 my-5 border rounded-lg bg-white">
                  <h1 className="text-3xl md:text-5xl font-bold leading-tight md:leading-[58px] text-darkGrey">
                    {displayPoll.name}
                  </h1>
                  <p className="text-lg md:text-xl text-darkGrey mt-3">
                    {displayPoll.description}
                  </p>
                  <div className="bg-[#EDF6FA] h-3 my-5" />

                  {pollOptions.length === 0 ? (
                    // Use placeholder options when the real poll isn't loaded yet
                    PLACEHOLDER_POLL.options.map((option) => (
                      <PollOptionRow
                        key={option._id}
                        option={option}
                        selected={false}
                        onSelect={() => {}}
                      />
                    ))
                  ) : (
                    pollOptions.map((option) => (
                      <PollOptionRow
                        key={option._id || option.name}
                        option={option}
                        selected={
                          selectedOption?._id === option._id ||
                          selectedOption?.name === option.name
                        }
                        onSelect={setSelectedOption}
                      />
                    ))
                  )}

                  <Button
                    text="Submit response"
                    color="primary"
                    className="w-full mt-4 h-[48px]"
                    customBgColor={selectedOption && canRespond ? "primary" : "grey"}
                    customTextColor={selectedOption && canRespond ? "white" : "black"}
                    onClick={handleSubmit}
                    disabled={!selectedOption || !canRespond}
                    fontWeight="bold"
                  />
                </div>
              </div>

              {/* self-start prevents the card from stretching to match the options column height */}
              <div className="flex flex-col items-center justify-center rounded-lg w-full min-h-[224px] h-fit self-start bg-white p-6 mt-6 md:mt-0">
                <Avatar
                  src={respondentDisplay.avatar}
                  alt={respondentDisplay.name}
                  size={80}
                  className="mb-3"
                />
                <h1 className="text-2xl font-bold text-[#0F75BC] text-center">
                  {respondentDisplay.name}
                </h1>
                <p className="text-lighterGrey">{respondentDisplay.role}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Login modal — shown until the user authenticates or proceeds anonymously.
          No way to dismiss it except by completing one of the actions inside. */}
      <DynamicPollsLoginModal
        isVisible={showLoginModal}
        onDismiss={handleDismissLoginModal}
        onLoginFailed={handleLoginFailed}
        onProceedAnonymously={handleProceedAnonymously}
      />

      <Success
        isVisible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        header="Response submitted"
        paragraph="Thank you. Your survey response has been recorded."
        buttonText="Close"
        onButtonClick={() => setShowSuccessModal(false)}
      />
    </div>
  );
};

export default Polls;
